<template>
  <internal-process
    class="fund-review-notes-wrapper"
    @go-back="goBack"
    :process-name="processTitle"
    :with-footer-background="false"
  >
    <template v-if="notesLoading">
      <mds-loader />
    </template>
    <template v-else-if="errorMsg">
     <div class="fund-error">
        <p>Something went wrong! We could not load the content for this fund.</p>
        <p>Please refresh the page or try again later.</p>
      </div>
    </template>
    <template v-else>
      <div class="fund-review-notes-container">
        <div class="fund-name-container">
          <h4 class="field-value">{{fundDetails.standardName}}</h4>
        </div>
        <div class="fund-details-container">
          <div class="fund-details fund-border">
            <span class="field-name">Provider Name:</span>
            <span class="field-value">{{fundDetails.clientName}}</span>
          </div>
          <div class="fund-details fund-border">
            <span class="field-name">FundID:</span>
            <span class="field-value">{{fundDetails.fundID}}</span>
          </div>
          <div class="fund-details fund-border">
            <span class="field-name">SecID:</span>
            <span class="field-value">{{fundDetails.secId}}</span>
          </div>
          <div class="fund-details">
            <span class="field-name">Ticker:</span>
            <span class="field-value">{{fundDetails.ticker}}</span>
          </div>
        </div>
        <mds-form size="small">
          <mds-textarea :maxlength="noteCharLimit" label="Add Notes" required
            placeholder="Input Text"
            v-model="noteText"
            :error ="noteError"
          ></mds-textarea>
          <div class="fund-note-char-count">
            {{noteCharLength}}/{{noteCharLimit}}
          </div>
          <mds-button
            variation="primary"
            @click.prevent="saveNote"
          >
            Save Note
          </mds-button>
        </mds-form>
      </div>
      <FundReviewNotesList ref="notesList" :fundId="fundId" />
    </template>
  </internal-process>
</template>

<script>
import MdsForm from '@mds/form';
import MdsTextarea from '@mds/textarea';
import { mapState } from 'vuex';
import FundReviewNotesList from '@/components/organisms/FundReviewNotesList';
import * as FundReviewService from '@/services/FundReview';

const NOTE_REVIEW_ACTION = 'Note Updated';

export default {

  name: 'FundReviewNotes',
  components: {
    MdsForm,
    MdsTextarea,
    FundReviewNotesList,
  },
  props: {
    fundId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      processTitle: 'Notes',
      noteCharLimit: 1000,
      noteText: '',
      noteError: false,
      notesLoading: false,
      fundDetails: '',
      errorMsg: false,
    };
  },
  created() {
    this.loadFundDetails();
  },
  computed: {
    ...mapState({
      selectedClient: (state) => state.grid.client.selected,
    }),
    noteCharLength() {
      return this.noteText.length;
    },
  },
  methods: {
    goBack() {
      if (this.selectedClient) {
        this.$router.push(
          {
            name: 'clientGrid',
            params: { clientId: this.selectedClient },
          },
        );
      } else {
        this.$router.push({ name: 'grid' });
      }
    },
    saveNote() {
      if (this.noteText) {
        this.noteError = false;
        this.callSaveNoteApi();
      } else {
        this.noteError = true;
      }
    },
    callSaveNoteApi() {
      const requestPayload = [{
        fiduciaryFundId: this.fundId,
        reviewStatus: '',
        reviewAction: NOTE_REVIEW_ACTION,
        notes: this.noteText,
      }];
      FundReviewService.addFundReview(requestPayload).then((response) => {
        if (response.status.success) {
          this.noteText = '';
          this.$store.dispatch('openNotification', {
            message: 'Note saved successfully.',
          });
          this.$refs.notesList.resetNotesListPagination();
        }
      }).catch((error) => {
        this.handleError(error);
      });
    },
    handleError(error) {
      const httpStatus = (error?.response?.status) ? (error?.response.status) : error?.status;
      if (httpStatus === 401) {
        this.$router.push('/login');
      } else {
        this.$store.dispatch('openNotification', {
          message: 'Note did not save. Please try again.',
          notificationType: 'error',
        });
      }
    },
    loadFundDetails() {
      this.notesLoading = true;
      FundReviewService.getFundDataPoints({
        fundId: this.fundId,
      }).then((response) => {
        this.notesLoading = false;
        this.fundDetails = { ...response.data.list[0] };
      }).catch((error) => {
        this.notesLoading = false;
        this.handleFundDetailsError(error);
      });
    },
    handleFundDetailsError(error) {
      const httpStatus = (error?.response?.status) ? (error?.response.status) : error?.status;
      if (httpStatus === 401) {
        this.$router.push('/login');
      } else {
        this.errorMsg = true;
      }
    },
  },
};
</script>
<style lang="scss">
.fund-review-notes-wrapper {
  .fund-error {
    text-align: center;
    margin-top: $mds-space-5-x;
    @include mds-body-text-l($bold: true);
  }
  .fund-review-notes-container {
    padding: $mds-space-7-x 0;
    .fund-name-container {
      .field-value {
        @include mds-body-text-l($bold: true);
        margin-bottom: $mds-space-3-x;
        margin-top: 0;
      }
    }
    .fund-details-container {
      margin-bottom: $mds-space-3-x;
      display: flex;
      .fund-details {
        @include mds-body-text-m();
        &.fund-border {
          padding-right: $mds-space-1-and-a-half-x;
          margin-right: $mds-space-1-and-a-half-x;
          position:relative;
        }
        &.fund-border:after {
          content: "";
          position: absolute;
          top: 10%;
          right: 0;
          width: 1px;
          height: 80%;
          background: $mds-color-neutral-67;
        }
        .field-name {
          margin-right: $mds-space-three-quarter-x;
          @include mds-body-text-m($bold: true);
        }
      }
    }
  }
  .fund-note-char-count {
    @include mds-body-text-m();
    text-align: right;
    float: right;
  }
}
</style>
